import { ReactComponent as DeleteIconRed } from '@/../../public/deleteIconRed.svg';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { MessageSquare } from 'lucide-react';
import { React, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AGENT_ENABLED } from '../featureFlags';

const VestmapBox = props => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deletionID, setDeletionID] = useState(null);

  async function deleteVestmap(id) {
    try {
      setLoader(true);
      await props.handleDeleteVestmap(id);
      setLoader(false);
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  }

  return (
    <Box
      key={props.id}
      sx={{
        alignSelf: 'center',
        width: '100%',
        maxWidth: props.isShared ? '400px' : 'none',
        borderRadius: '1.21144rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        marginY: props.isShared ? '0' : '1rem',
        boxShadow: '5px 20px 30px 0px rgba(229, 229, 229,0.7)',
      }}
    >
      {/*   PopUp asking for delete confirmation confirmation */}
      <Dialog
        open={deleteConfirmation}
        onClose={() => {
          setDeletionID(null);
          setDeleteConfirmation(false);
        }}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        PaperProps={{
          sx: {
            borderRadius: '1.25rem',
          },
        }}
      >
        <DialogTitle id='alert-dialog-title'>{'Remove Report'}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to remove this report?{' '}
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            onClick={() => {
              setDeletionID(null);
              setDeleteConfirmation(false);
            }}
            sx={{
              color: '#39585C',
              border: '1px solid #39585C',
              borderRadius: '0.75rem',
              height: '2.5rem',
            }}
          >
            No
          </Button>
          <Button
            onClick={async () => {
              console.log('Deleting: ', deletionID);
              deleteVestmap(deletionID);
              setDeleteConfirmation(false);
              setDeletionID(null);
            }}
            color='error'
            sx={{
              border: '1px solid red',
              borderRadius: '0.75rem',
              height: '2.5rem',
              ':hover': {
                bgcolor: 'red',
                color: 'white',
              },
            }}
          >
            {loader ? <CircularProgress size={15} color='error' /> : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          width: '100%',
          height: '150px',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={
            props?.record?.assets?.maps?.google_map ||
            `https://oldapp.vestmap.com/google_img/${props?.record?.google_image_url}`
          }
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = require('../img/defaultMap.png');
          }}
          alt='Vestmap Default Map'
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'cover',
          }}
        />
      </Box>

      {(props?.record?.template ||
        props?.record?.templateName ||
        props?.record?.templateID) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '1rem',
          }}
        >
          <Box
            sx={[
              {
                fontFamily: 'Lato',
                fontSize: '1rem',
                padding: '0.5rem 0.5rem 0.5rem 0.5rem',
                borderRadius: '0.7rem',
              },

              props?.record?.template?.theme || props?.record?.theme,
            ]}
          >
            {props?.record?.template?.templateName ||
              props?.record?.templateName ||
              props?.record?.templateID ||
              'Template'}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          padding: '1.25rem 0',
          textAlign: 'center',
        }}
      >
        <Typography
          variant='h6'
          component='h3'
          textAlign={'center'}
          sx={{
            fontFamily: '"Lato", sans-serif',
            paddingX: '1rem',
            fontSize: '1.25rem',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '140%',
            color: '#323142',
          }}
        >
          {props?.record?.address_info?.address ||
            props?.record?.addressInfo?.address}
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
        }}
      >
        {AGENT_ENABLED && props?.record?.chatID && !props.isShared && (
          <Box
            sx={{
              width: '52px',
              height: '3rem',
              border: '2px solid #176a9e',
              borderRadius: '12px',

              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (loader) return;
              navigate(`/agent?chatID=${props.record.chatID}`);
            }}
          >
            {loader ? (
              <CircularProgress size={20} color='error' />
            ) : (
              <MessageSquare
                style={{
                  color: '#176a9e',
                }}
              />
            )}
          </Box>
        )}

        <Button
          target='_blank'
          startIcon={<LaunchIcon />}
          variant='outlined'
          size='large'
          onClick={() => props.handleModalOpen(props.record)}
          sx={{
            border: '2px solid #39585C',
            borderRadius: '0.75rem',
            color: 'black',
            textTransform: 'capitalize',
            fontFamily: '"Lato", sans-serif',
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '150%',
            ':hover': {
              border: '2px solid #39585C',
              bgcolor: '#39585C',
              color: 'white',
            },
          }}
        >
          View Report
        </Button>

        {!props.isShared && (
          <Box
            sx={{
              width: '52px',
              height: '3rem',
              border: '2px solid #D32F2F',
              borderRadius: '12px',

              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (loader) return;

              setDeletionID(props.record._id);
              setDeleteConfirmation(true);
            }}
          >
            {loader ? (
              <CircularProgress size={20} color='error' />
            ) : (
              <DeleteIconRed />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VestmapBox;
